import { FC } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { Icon } from '@opallabs/lapidary'
import { AccountName } from '../../../../../AccountName'
import { ContentAvatar } from '../../../../../ContentAvatar'
import { ContentTextInput } from '../../../../../ContentTextInput'
import { ScheduledTime } from '../../../../../ScheduledTime'
import type { Asset } from '../../../../helpers/types'
import { DocumentCarousel } from '../../components/DocumentCarousel'
import { LinkedInDocumentDisplayProps } from '../../types'
import * as styles from './styles'

const PostHeader: FC<{
  accountImage: Asset | null
  accountName: string
  scheduledAt?: string
  timezone?: string
}> = ({ accountImage, accountName, scheduledAt, timezone }) => (
  <div css={styles.header}>
    <ContentAvatar
      assetReference={accountImage}
      size={48}
      style={styles.avatar}
    />
    <div css={styles.headerInfo}>
      <AccountName
        name={accountName}
        noAccountColor="#fab14d"
        size={14}
        style={styles.accountNameStyle}
      />
      <span css={styles.headerMeta}>
        <ScheduledTime
          color="rgba(0, 0, 0, 0.6)"
          dateFormat="MMM d, yyyy"
          fontSize={12}
          timestamp={scheduledAt}
          timezone={timezone}
        />
      </span>
    </div>
  </div>
)

const PostContent: FC<{
  content: string
  onChange: (value: string) => void
  readOnly: boolean
}> = ({ content, onChange, readOnly }) => (
  <div css={styles.contentWrapper}>
    <ContentTextInput
      ariaLabel="Post content"
      characterLimit={3000}
      data-readonly={readOnly}
      minRows={1}
      maxRows={10}
      placeholder="What do you want to talk about?"
      readOnly={readOnly}
      style={styles.contentTextInputStyle}
      value={content}
      onChange={onChange}
    />
  </div>
)

const PostActions = () => (
  <div css={styles.actions}>
    <div css={styles.actionButton}>
      <Icon glyph="linkedin-desktop-like" useHypernova width={16} height={16} />
      <span>Like</span>
    </div>
    <div css={styles.actionButton}>
      <Icon
        glyph="linkedin-desktop-comment"
        useHypernova
        width={16}
        height={16}
      />
      <span>Comment</span>
    </div>
    <div css={styles.actionButton}>
      <Icon
        glyph="linkedin-desktop-share"
        useHypernova
        width={16}
        height={16}
      />
      <span>Repost</span>
    </div>
    <div css={styles.actionButton}>
      <Icon
        glyph="linkedin-desktop-share"
        useHypernova
        width={16}
        height={16}
      />
      <span>Send</span>
    </div>
  </div>
)

export const DesktopNewsFeed: FC<LinkedInDocumentDisplayProps> = ({
  accountImage,
  accountName,
  assets,
  content,
  documentTitle,
  isPresenting,
  onContentChange,
  onDocumentTitleChange,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  scheduledAt,
  timezone,
}) => {
  const pdfAsset: AssetReference | null = assets?.[0] ?? null

  return (
    <div css={styles.container}>
      <PostHeader
        accountImage={accountImage}
        accountName={accountName}
        scheduledAt={scheduledAt}
        timezone={timezone}
      />

      <PostContent
        content={content}
        onChange={onContentChange}
        readOnly={readOnly || isPresenting}
      />

      <DocumentCarousel
        assetReference={pdfAsset}
        documentTitle={documentTitle}
        isPresenting={isPresenting}
        onDocumentTitleChange={onDocumentTitleChange}
        onExpandAsset={onExpandAsset}
        onRemoveAsset={onRemoveAsset}
        readOnly={readOnly}
        width={styles.carouselWidth}
      />

      <PostActions />
    </div>
  )
}
